import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "../lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
// import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { format } from "date-fns";
import { CheckCircle, XCircle, Clock, FileCheck, Activity } from "lucide-react";

export default function SlaMonitoring() {
  const [activeTab, setActiveTab] = useState<string>("sla-definitions");
  
  // SLA definition form state
  const [slaForm, setSlaForm] = useState({
    name: "",
    description: "",
    metricType: "uptime",
    threshold: "99.9",
    interval: "monthly",
    criticalityLevel: "high"
  });
  
  // SLA assignment form state
  const [assignmentForm, setAssignmentForm] = useState({
    deviceId: "",
    slaDefinitionId: "",
    startDate: format(new Date(), "yyyy-MM-dd"),
    notes: ""
  });

  // Get all SLA definitions
  const { data: slaDefinitionsData = { items: [] }, isLoading: isLoadingSlas } = useQuery({
    queryKey: ["/api/sla-definitions"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/sla-definitions");
        if (!res.ok) {
          throw new Error("Failed to fetch SLA definitions");
        }
        const data = await res.json();
        // Ensure we have an array, even if the API returns an object
        return Array.isArray(data) ? { items: data } : { items: data.items || [] };
      } catch (error) {
        console.error("Error fetching SLA definitions:", error);
        return { items: [] };
      }
    },
  });
  
  // Extract items array to ensure we're always working with an array
  const slaDefinitions = slaDefinitionsData.items || [];
  
  // Get all devices
  const { data: devices = [], error: devicesError } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: () => fetch("/api/devices").then(res => res.json()),
  });
  
  // Get SLA violations
  const { data: violations = [], isLoading: isLoadingViolations, error: violationsError } = useQuery({
    queryKey: ["/api/sla-violations"],
    queryFn: () => fetch("/api/sla-violations").then(res => res.json()),
  });
  
  // Get Device SLAs
  const { data: deviceSlas = [], isLoading: isLoadingDeviceSlas, error: deviceSlasError } = useQuery({
    queryKey: ["/api/device-slas"],
    queryFn: () => fetch("/api/device-slas").then(res => res.json()),
  });

  // Create SLA definition
  const createSlaMutation = useMutation({
    mutationFn: (data: any) => {
      return fetch("/api/sla-definitions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }).then(res => res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sla-definitions"] });
      resetSlaForm();
    },
  });
  
  // Assign SLA to device
  const assignSlaMutation = useMutation({
    mutationFn: (data: any) => {
      return fetch("/api/device-slas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }).then(res => res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/device-slas"] });
      resetAssignmentForm();
    },
  });

  // Acknowledge SLA violation
  const acknowledgeViolationMutation = useMutation({
    mutationFn: (violationId: number) => {
      return fetch(`/api/sla-violations/${violationId}/acknowledge`, {
        method: "POST",
      }).then(res => res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sla-violations"] });
    },
  });
  
  // Resolve SLA violation
  const resolveViolationMutation = useMutation({
    mutationFn: (violationId: number) => {
      return fetch(`/api/sla-violations/${violationId}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endTime: new Date().toISOString() }),
      }).then(res => res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sla-violations"] });
    },
  });

  // Handle SLA form change
  const handleSlaFormChange = (field: string, value: string) => {
    setSlaForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };
  
  // Handle assignment form change
  const handleAssignmentFormChange = (field: string, value: string) => {
    setAssignmentForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Reset SLA form
  const resetSlaForm = () => {
    setSlaForm({
      name: "",
      description: "",
      metricType: "uptime",
      threshold: "99.9",
      interval: "monthly",
      criticalityLevel: "high"
    });
  };
  
  // Reset assignment form
  const resetAssignmentForm = () => {
    setAssignmentForm({
      deviceId: "",
      slaDefinitionId: "",
      startDate: format(new Date(), "yyyy-MM-dd"),
      notes: ""
    });
  };

  // Handle creating a new SLA definition
  const handleCreateSla = (e: React.FormEvent) => {
    e.preventDefault();
    createSlaMutation.mutate({
      name: slaForm.name,
      description: slaForm.description,
      metricType: slaForm.metricType,
      threshold: parseFloat(slaForm.threshold),
      interval: slaForm.interval,
      criticalityLevel: slaForm.criticalityLevel
    });
  };
  
  // Handle assigning SLA to device
  const handleAssignSla = (e: React.FormEvent) => {
    e.preventDefault();
    assignSlaMutation.mutate({
      deviceId: parseInt(assignmentForm.deviceId),
      slaDefinitionId: parseInt(assignmentForm.slaDefinitionId),
      startDate: new Date(assignmentForm.startDate).toISOString(),
      notes: assignmentForm.notes
    });
  };

  // Handle acknowledging a violation
  const handleAcknowledgeViolation = (violationId: number) => {
    acknowledgeViolationMutation.mutate(violationId);
  };
  
  // Handle resolving a violation
  const handleResolveViolation = (violationId: number) => {
    resolveViolationMutation.mutate(violationId);
  };

  // Get device name by ID
  const getDeviceName = (deviceId: number) => {
    const device = devices.find((d: any) => d.id === deviceId);
    return device ? device.name : `Device ${deviceId}`;
  };
  
  // Get SLA name by ID
  const getSlaName = (slaId: number) => {
    const sla = slaDefinitions.find((s: any) => s.id === slaId);
    return sla ? sla.name : `SLA ${slaId}`;
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "PPp");
    } catch (e) {
      return "Invalid date";
    }
  };

  // Get badge for SLA status
  const getSlaStatusBadge = (violation: any) => {
    if (violation.resolved) {
      return <Badge variant="outline" className="text-green-500 border-green-500">Resolved</Badge>;
    }
    if (violation.acknowledged) {
      return <Badge variant="outline" className="text-blue-500 border-blue-500">Acknowledged</Badge>;
    }
    return <Badge variant="destructive">Active</Badge>;
  };
  
  // Get SLA criticality badge
  const getSlaCriticalityBadge = (level: string) => {
    switch (level) {
      case "critical":
        return <Badge variant="destructive">Critical</Badge>;
      case "high":
        return <Badge className="bg-orange-500">High</Badge>;
      case "medium":
        return <Badge variant="secondary">Medium</Badge>;
      case "low":
        return <Badge variant="outline">Low</Badge>;
      default:
        return <Badge variant="outline">Standard</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-3xl font-bold mb-6">SLA Monitoring</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="sla-definitions">SLA Definitions</TabsTrigger>
          <TabsTrigger value="device-slas">Device SLAs</TabsTrigger>
          <TabsTrigger value="violations">SLA Violations</TabsTrigger>
        </TabsList>
        
        {/* SLA Definitions Tab */}
        <TabsContent value="sla-definitions">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>SLA Definitions</CardTitle>
                  <CardDescription>
                    Service Level Agreement templates for your infrastructure
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingSlas ? (
                    <div className="text-center py-4">Loading SLA definitions...</div>
                  ) : slaDefinitions.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No SLA definitions found. Create your first SLA definition.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Metric</TableHead>
                          <TableHead>Threshold</TableHead>
                          <TableHead>Interval</TableHead>
                          <TableHead>Criticality</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {slaDefinitions.map((sla: any) => (
                          <TableRow key={sla.id}>
                            <TableCell>
                              <div className="font-medium">{sla.name}</div>
                              <div className="text-sm text-muted-foreground mt-1">
                                {sla.description}
                              </div>
                            </TableCell>
                            <TableCell>{sla.metricType}</TableCell>
                            <TableCell>
                              {sla.metricType === "uptime" || sla.metricType === "availability" 
                                ? `${sla.threshold}%` 
                                : sla.metricType === "response_time" 
                                ? `${sla.threshold}ms` 
                                : sla.threshold}
                            </TableCell>
                            <TableCell className="capitalize">{sla.interval}</TableCell>
                            <TableCell>{getSlaCriticalityBadge(sla.criticalityLevel)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Create New SLA Definition</CardTitle>
                  <CardDescription>
                    Define a new Service Level Agreement
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateSla} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sla-name">Name</Label>
                      <Input 
                        id="sla-name" 
                        placeholder="e.g. Enterprise Uptime SLA"
                        value={slaForm.name}
                        onChange={(e) => handleSlaFormChange("name", e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="sla-description">Description</Label>
                      <Textarea 
                        id="sla-description" 
                        placeholder="Brief description of this SLA"
                        value={slaForm.description}
                        onChange={(e) => handleSlaFormChange("description", e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="sla-metric">Metric Type</Label>
                      <Select 
                        value={slaForm.metricType} 
                        onValueChange={(value) => handleSlaFormChange("metricType", value)}
                      >
                        <SelectTrigger id="sla-metric">
                          <SelectValue placeholder="Select metric type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="uptime">Uptime</SelectItem>
                          <SelectItem value="availability">Availability</SelectItem>
                          <SelectItem value="response_time">Response Time</SelectItem>
                          <SelectItem value="error_rate">Error Rate</SelectItem>
                          <SelectItem value="throughput">Throughput</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="sla-threshold">Threshold</Label>
                      <div className="flex items-center">
                        <Input 
                          id="sla-threshold" 
                          type="text"
                          value={slaForm.threshold}
                          onChange={(e) => handleSlaFormChange("threshold", e.target.value)}
                          required
                        />
                        <span className="ml-2">
                          {slaForm.metricType === "uptime" || slaForm.metricType === "availability" 
                            ? "%" 
                            : slaForm.metricType === "response_time" 
                            ? "ms" 
                            : ""}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="sla-interval">Measurement Interval</Label>
                      <Select 
                        value={slaForm.interval} 
                        onValueChange={(value) => handleSlaFormChange("interval", value)}
                      >
                        <SelectTrigger id="sla-interval">
                          <SelectValue placeholder="Select interval" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="sla-criticality">Criticality Level</Label>
                      <Select 
                        value={slaForm.criticalityLevel} 
                        onValueChange={(value) => handleSlaFormChange("criticalityLevel", value)}
                      >
                        <SelectTrigger id="sla-criticality">
                          <SelectValue placeholder="Select criticality" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="critical">Critical</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={createSlaMutation.isPending}
                    >
                      {createSlaMutation.isPending ? "Creating..." : "Create SLA Definition"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Device SLAs Tab */}
        <TabsContent value="device-slas">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Device SLA Assignments</CardTitle>
                  <CardDescription>
                    Active SLAs assigned to network devices
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {deviceSlasError ? (
                    <div className="text-red-500 text-center py-4">Failed to load device SLAs. Please try again later.</div>
                  ) : !Array.isArray(deviceSlas) ? (
                    <div className="text-red-500 text-center py-4">Device SLAs data is invalid.</div>
                  ) : isLoadingDeviceSlas ? (
                    <div className="text-center py-4">Loading device SLAs...</div>
                  ) : deviceSlas.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No SLAs assigned to devices. Assign an SLA to a device.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Device</TableHead>
                          <TableHead>SLA</TableHead>
                          <TableHead>Start Date</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deviceSlas.map((assignment: any) => (
                          <TableRow key={assignment.id}>
                            <TableCell>
                              {getDeviceName(assignment.deviceId)}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{getSlaName(assignment.slaDefinitionId)}</div>
                              {assignment.notes && (
                                <div className="text-sm text-muted-foreground mt-1">
                                  {assignment.notes}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>{formatDate(assignment.startDate)}</TableCell>
                            <TableCell>
                              {assignment.active ? (
                                <Badge variant="outline" className="text-green-500 border-green-500">
                                  <CheckCircle className="mr-1 h-3 w-3" /> Active
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  <XCircle className="mr-1 h-3 w-3" /> Inactive
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Assign SLA to Device</CardTitle>
                  <CardDescription>
                    Apply an SLA definition to a network device
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAssignSla} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="assign-device">Device</Label>
                      <Select 
                        value={assignmentForm.deviceId} 
                        onValueChange={(value) => handleAssignmentFormChange("deviceId", value)}
                        required
                      >
                        <SelectTrigger id="assign-device">
                          <SelectValue placeholder="Select device" />
                        </SelectTrigger>
                        <SelectContent>
                          {devices.map((device: any) => (
                            device && device.id !== undefined ? (
                              <SelectItem key={device.id} value={device.id.toString()}>
                                {device.name} ({device.ipAddress})
                              </SelectItem>
                            ) : null
                          ))}
                        </SelectContent>
                      </Select>
                      {devicesError && (
                        <div className="text-red-500 text-center py-4">Failed to load devices. Please try again later.</div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="assign-sla">SLA Definition</Label>
                      <Select 
                        value={assignmentForm.slaDefinitionId} 
                        onValueChange={(value) => handleAssignmentFormChange("slaDefinitionId", value)}
                        required
                      >
                        <SelectTrigger id="assign-sla">
                          <SelectValue placeholder="Select SLA" />
                        </SelectTrigger>
                        <SelectContent>
                          {slaDefinitions.map((sla: any) => (
                            <SelectItem key={sla.id} value={sla.id.toString()}>
                              {sla.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="assign-start-date">Start Date</Label>
                      <Input 
                        id="assign-start-date" 
                        type="date"
                        value={assignmentForm.startDate}
                        onChange={(e) => handleAssignmentFormChange("startDate", e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="assign-notes">Notes (Optional)</Label>
                      <Textarea 
                        id="assign-notes" 
                        placeholder="Any additional notes about this assignment"
                        value={assignmentForm.notes}
                        onChange={(e) => handleAssignmentFormChange("notes", e.target.value)}
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={
                        assignSlaMutation.isPending || 
                        !assignmentForm.deviceId || 
                        !assignmentForm.slaDefinitionId
                      }
                    >
                      {assignSlaMutation.isPending ? "Assigning..." : "Assign SLA"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* SLA Violations Tab */}
        <TabsContent value="violations">
          <Card>
            <CardHeader>
              <CardTitle>SLA Violations</CardTitle>
              <CardDescription>
                Breaches of Service Level Agreements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {violationsError ? (
                <div className="text-red-500 text-center py-4">Failed to load SLA violations. Please try again later.</div>
              ) : !Array.isArray(violations) ? (
                <div className="text-red-500 text-center py-4">SLA violations data is invalid.</div>
              ) : isLoadingViolations ? (
                <div className="text-center py-4">Loading SLA violations...</div>
              ) : violations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <FileCheck className="h-12 w-12 text-green-500 mb-4" />
                  <p className="text-lg font-medium">No SLA Violations</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    All services are operating within their defined SLAs
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {violations.map((violation: any) => (
                    <Card key={violation.id} className="overflow-hidden">
                      <div className={`p-4 ${
                        violation.resolved 
                          ? "bg-green-50 dark:bg-green-950/20" 
                          : violation.acknowledged 
                          ? "bg-blue-50 dark:bg-blue-950/20" 
                          : "bg-red-50 dark:bg-red-950/20"
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-base font-medium">
                              SLA Violation: {getDeviceName(violation.deviceId)}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {violation.description || `Violation of ${getSlaName(violation.slaDefinitionId)}`}
                            </p>
                            
                            <div className="flex gap-3 mt-3">
                              <Badge variant="outline" className="flex items-center">
                                <Activity className="mr-1 h-3 w-3" />
                                {getSlaName(violation.slaDefinitionId)}
                              </Badge>
                              <Badge variant="outline" className="flex items-center">
                                <Clock className="mr-1 h-3 w-3" />
                                {formatDate(violation.startTime)}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-end">
                            {getSlaStatusBadge(violation)}
                            
                            {violation.endTime && (
                              <span className="text-xs text-muted-foreground mt-1">
                                Resolved: {formatDate(violation.endTime)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {!violation.resolved && (
                          <div className="mt-4 flex justify-end gap-2">
                            {!violation.acknowledged && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleAcknowledgeViolation(violation.id)}
                                disabled={acknowledgeViolationMutation.isPending}
                              >
                                Acknowledge
                              </Button>
                            )}
                            
                            <Button 
                              variant="default" 
                              size="sm"
                              onClick={() => handleResolveViolation(violation.id)}
                              disabled={resolveViolationMutation.isPending}
                            >
                              Mark as Resolved
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}